/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import { logError } from '../helper/logger';
import ApiClient from './ApiClient';

const DEFAULT_CACHE_OPERATION_TIMEOUT_MS = 2500;

const withCacheTimeout = (operation, timeoutMs, operationName) => {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return Promise.resolve().then(operation);
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      const error = new Error(
        `Cache operation timed out: ${operationName || 'unknown'}`
      );
      error.code = 'CACHE_OPERATION_TIMEOUT';
      reject(error);
    }, timeoutMs);

    Promise.resolve()
      .then(operation)
      .then(
        (result) => {
          clearTimeout(timeout);
          resolve(result);
        },
        (error) => {
          clearTimeout(timeout);
          reject(error);
        }
      );
  });
};

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    this.DataManagerClass = options.dataManagerClass || DataManager;
    this.cacheOperationTimeoutMs =
      options.cacheOperationTimeoutMs ?? DEFAULT_CACHE_OPERATION_TIMEOUT_MS;
    this.dataManagerAccountId = null;
    this.dataManagerInstance = null;
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get dataManager() {
    return this.getDataManager(this.accountIdFromRoute);
  }

  getDataManager(accountId) {
    if (!this.dataManagerInstance || this.dataManagerAccountId !== accountId) {
      this.dataManagerAccountId = accountId;
      this.dataManagerInstance = new this.DataManagerClass(accountId);
    }

    return this.dataManagerInstance;
  }

  get(cache = false) {
    if (cache) {
      return this.getFromCache();
    }

    return this.getFromNetwork();
  }

  getFromNetwork() {
    return axios.get(this.url);
  }

  getCacheContext() {
    const accountId = this.accountIdFromRoute;
    return {
      accountId,
      dataManager: this.getDataManager(accountId),
      networkUrl: this.url,
    };
  }

  // The route may change while IndexedDB is resolving. Keeping the URL and
  // manager together prevents a response for one account from being written
  // into another account's cache.
  // eslint-disable-next-line class-methods-use-this
  getFromNetworkForContext({ networkUrl }) {
    return axios.get(networkUrl);
  }

  logCacheFailure(operation, context, error) {
    logError(
      `[CacheEnabledApiClient] ${operation}; using the network response`,
      {
        accountId: context.accountId,
        modelName: this.cacheModelName,
      },
      error
    );
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  runCacheOperation(operation, operationName) {
    return withCacheTimeout(
      operation,
      this.cacheOperationTimeoutMs,
      operationName
    );
  }

  validateCacheKeyWithTimeout(cacheKeyFromApi, dataManager = this.dataManager) {
    return this.runCacheOperation(
      () => this.validateCacheKey(cacheKeyFromApi, dataManager),
      'cache key validation'
    );
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    const context = this.getCacheContext();
    let cacheKeyFromApi = null;
    let localData = [];

    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await this.runCacheOperation(
        () => context.dataManager.initDb(),
        'database open'
      );

      const { data } = await axios.get(
        `/api/v1/accounts/${context.accountId}/cache_keys`
      );
      cacheKeyFromApi = data.cache_keys[this.cacheModelName];
      const isCacheValid = await this.validateCacheKeyWithTimeout(
        cacheKeyFromApi,
        context.dataManager
      );

      if (isCacheValid) {
        localData = await this.runCacheOperation(
          () =>
            context.dataManager.get({
              modelName: this.cacheModelName,
            }),
          'cache read'
        );
      }
    } catch (error) {
      this.logCacheFailure('cache hydration failed', context, error);
      return this.getFromNetworkForContext(context);
    }

    if (!Array.isArray(localData) || localData.length === 0) {
      return this.refetchAndCommit(cacheKeyFromApi, context);
    }

    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null, cacheContext = null) {
    const context = cacheContext || this.getCacheContext();
    const response = await this.getFromNetworkForContext(context);

    try {
      await this.runCacheOperation(async () => {
        await context.dataManager.initDb();

        // Never publish the key before all records are durable. Otherwise a
        // concurrent deep-link can observe a valid key between clear and put
        // and hydrate the Vuex store with an empty inbox collection.
        await context.dataManager.replace({
          modelName: this.cacheModelName,
          data: this.extractDataFromResponse(response),
        });

        await context.dataManager.setCacheKeys({
          [this.cacheModelName]: newKey,
        });
      }, 'cache persistence');
    } catch (error) {
      this.logCacheFailure('cache persistence failed', context, error);
    }

    return response;
  }

  async refreshFromNetwork() {
    const context = this.getCacheContext();
    let cacheKeyFromApi = null;

    try {
      const { data } = await axios.get(
        `/api/v1/accounts/${context.accountId}/cache_keys`
      );
      cacheKeyFromApi = data.cache_keys[this.cacheModelName];
    } catch (error) {
      this.logCacheFailure('cache-key refresh failed', context, error);
    }

    return this.refetchAndCommit(cacheKeyFromApi, context);
  }

  async validateCacheKey(cacheKeyFromApi, dataManager = this.dataManager) {
    if (!dataManager.db) {
      await dataManager.initDb();
    }

    const cachekey = await dataManager.getCacheKey(this.cacheModelName);
    return cacheKeyFromApi === cachekey;
  }
}

export default CacheEnabledApiClient;
